
<template>
  <div
    class="unlock-character"
    :class="[
      `unlock-level-${level}`,
      `unlock-tone-${normalizedTone}`,
      { 'unlock-ceo': variant === 'ceo' }
    ]"
    :title="description"
  >

    <!-- DECORATION -->

    <div class="unlock-accent"></div>
    <div class="unlock-glow"></div>

    <!-- LEVEL ICON -->

    <div
      v-if="level === 1"
      class="unlock-level-icon"
    >
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M12 3L14.7 8.5L20.8 9.4L16.4 13.7L17.4 19.8L12 17L6.6 19.8L7.6 13.7L3.2 9.4L9.3 8.5L12 3Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <!-- CIRCULAR PORTRAIT -->

    <div class="unlock-portrait">

      <div class="portrait-inner">

        <svg
          class="character-silhouette"
          viewBox="0 0 100 100"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="50"
            cy="34"
            r="17"
            fill="currentColor"
          />

          <path
            d="M15 96C16 72 30 60 50 60C70 60 84 72 85 96"
            fill="currentColor"
          />
        </svg>

      </div>

      <span class="question-badge">
        ?
      </span>

    </div>

    <!-- INFORMATION -->

    <div class="unlock-information">

      <span class="unlock-role">
        {{ roleLabel }}
      </span>

      <strong class="unlock-name">
        {{ level === 1 ? "Не указано" : "Не раскрыт" }}
      </strong>

      <span
        v-if="level !== 1"
        class="unlock-description"
      >
        Ожидает участника
      </span>

    </div>

    <!-- STATUS -->

    <div
      v-if="level !== 1"
      class="unlock-status"
    >
      <span class="unlock-status-dot"></span>
      Не представлен
    </div>

    <span
      v-else
      class="unlock-ceo-sparkle"
      aria-hidden="true"
    >
      ✦
    </span>

  </div>
</template>

<script setup>
import { computed } from "vue";

// ============================================================
// PROPS
// ============================================================

const props = defineProps({
  level: {
    type: Number,
    default: 3,
    validator: value => [1, 2, 3].includes(value)
  },

  variant: {
    type: String,
    default: "node",
    validator: value =>
      ["node", "ceo"].includes(value)
  },

  tone: {
    type: Number,
    default: 0
  }
});

// ============================================================
// TONE
// ============================================================

const normalizedTone = computed(() => {
  const value = Number.isFinite(props.tone)
    ? Math.trunc(props.tone)
    : 0;

  return ((value % 4) + 4) % 4;
});

// ============================================================
// LEVEL LABEL
// ============================================================

const roleLabel = computed(() => {
  switch (props.level) {
    case 1:
      return "УРОВЕНЬ 01";

    case 2:
      return "РУКОВОДИТЕЛЬ";

    default:
      return "СПЕЦИАЛИСТ";
  }
});

// ============================================================
// TOOLTIP
// ============================================================

const description = computed(() => {
  return props.level === 1
    ? "Информация о руководстве не указана"
    : "Участник пока не представлен в HR3";
});
</script>

<style scoped>

/* ============================================================
   BASE CHARACTER
============================================================ */

.unlock-character {
  --character-color: #678fbe;
  --character-background: #e6effa;
  --character-background-light: #f5f9ff;

  position: relative;

  min-width: 0;
  min-height: 188px;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 18px 7px 12px;

  overflow: hidden;

  color: var(--text, #243247);

  background:
    linear-gradient(
      155deg,
      var(--surface, #ffffff),
      var(--surface-soft, #f8faff)
    );

  border: 1px dashed var(
    --border,
    rgba(40, 78, 134, 0.12)
  );

  border-radius: 25px;

  box-shadow:
    0 10px 26px rgba(31, 67, 120, 0.035);

  font-family: "Eastman-Regular", sans-serif;
  text-align: center;

  cursor: default;

  transition:
    border-color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.unlock-character:hover {
  transform: translateY(-3px);

  border-color: rgba(77, 139, 221, 0.35);

  box-shadow:
    0 16px 32px rgba(37, 93, 175, 0.07);
}

/* ============================================================
   LEVEL COLORS
============================================================ */

.unlock-level-1 {
  --character-color: #b89357;
  --character-background: #eee3d0;
  --character-background-light: #faf5e9;
}

.unlock-level-2 {
  --character-color: #7298c9;
  --character-background: #e1ecfa;
  --character-background-light: #f4f8ff;
}

.unlock-level-3 {
  --character-color: #75aa9d;
  --character-background: #e0f0e9;
  --character-background-light: #f2faf6;
}

/* ============================================================
   COLOR VARIATIONS
============================================================ */

.unlock-tone-1 {
  --character-background: #e9e7f8;
  --character-background-light: #f7f6ff;
  --character-color: #9990c3;
}

.unlock-tone-2 {
  --character-background: #e0ebf4;
  --character-background-light: #f3f8fc;
  --character-color: #81a4bc;
}

.unlock-tone-3 {
  --character-background: #e4f0e6;
  --character-background-light: #f5faf6;
  --character-color: #87af96;
}

/* ============================================================
   DECORATION
============================================================ */

.unlock-accent {
  position: absolute;

  top: 0;

  width: 37px;
  height: 2px;

  border-radius: 0 0 10px 10px;

  background: var(--character-color);

  opacity: 0.42;
}

.unlock-glow {
  position: absolute;

  width: 120px;
  height: 120px;

  top: -80px;
  right: -60px;

  border-radius: 50%;

  background: var(--character-color);

  opacity: 0.045;

  pointer-events: none;
}

/* ============================================================
   ROUND LEVEL ICON
============================================================ */

.unlock-level-icon {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-bottom: 11px;

  background: rgba(213, 164, 75, 0.12);
  color: #c59a50;

  border-radius: 50%;
}

.unlock-level-icon svg {
  width: 19px;
  height: 19px;
}

/* ============================================================
   CIRCULAR PORTRAIT
============================================================ */

.unlock-portrait {
  position: relative;

  width: 72px;
  height: 72px;

  flex-shrink: 0;

  margin-bottom: 13px;
}

.portrait-inner {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border-radius: 50%;

  background:
    linear-gradient(
      145deg,
      var(--character-background),
      var(--character-background-light)
    );

  border: 1px solid rgba(135, 160, 195, 0.08);

  box-shadow:
    0 0 0 4px rgba(120, 155, 204, 0.045);
}

.character-silhouette {
  width: 88%;
  height: 88%;

  color: var(--character-color);

  opacity: 0.28;

  filter:
    drop-shadow(
      0 3px 4px rgba(75, 104, 145, 0.06)
    );

  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.unlock-character:hover .character-silhouette {
  opacity: 0.42;

  transform: scale(1.06);
}

/* ============================================================
   QUESTION BADGE
============================================================ */

.question-badge {
  position: absolute;

  right: -7px;
  bottom: -5px;

  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #82a8d5,
      #567dab
    );

  border: 3px solid var(--surface, #ffffff);
  border-radius: 50%;

  box-shadow:
    0 4px 12px rgba(63, 104, 162, 0.15);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
}

/* ============================================================
   INFORMATION
============================================================ */

.unlock-information {
  width: 100%;
  min-width: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 6px;
}

.unlock-role {
  color: var(--character-color);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;

  letter-spacing: 0.5px;
}

.unlock-name {
  width: 100%;

  color: var(--muted, #8896aa);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  font-weight: 500;
  line-height: 1.35;
}

.unlock-description {
  width: 100%;

  color: var(--subtle, #a3aec0);

  font-size: 9px;
  line-height: 1.4;
}

/* ============================================================
   STATUS
============================================================ */

.unlock-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  margin-top: auto;
  padding-top: 11px;

  color: var(--subtle, #a3aec0);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;
}

.unlock-status-dot {
  width: 5px;
  height: 5px;

  flex-shrink: 0;

  border-radius: 50%;

  background: #b4c0d0;
}

/* ============================================================
   FIRST LEVEL — LARGE VARIANT
============================================================ */

.unlock-ceo {
  width: 265px;
  min-height: 275px;

  padding: 24px 20px 22px;

  border-style: solid;
  border-color: rgba(213, 166, 85, 0.24);

  border-radius: 32px;

  box-shadow:
    0 16px 43px rgba(126, 102, 61, 0.07);
}

.unlock-ceo .unlock-accent {
  width: 90px;

  background: #d3a453;

  opacity: 0.8;
}

.unlock-ceo .unlock-portrait {
  width: 100px;
  height: 100px;

  margin-bottom: 18px;
}

.unlock-ceo .portrait-inner {
  box-shadow:
    0 0 0 5px rgba(211, 164, 83, 0.08);
}

.unlock-ceo .question-badge {
  width: 29px;
  height: 29px;

  background:
    linear-gradient(
      145deg,
      #e0bb73,
      #b58b46
    );
}

.unlock-ceo .unlock-role {
  color: #bd9452;

  font-size: 9px;
  letter-spacing: 1.2px;
}

.unlock-ceo .unlock-name {
  margin-top: 3px;

  color: var(--text, #243247);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;
}

.unlock-ceo-sparkle {
  position: absolute;

  top: 19px;
  right: 20px;

  color: rgba(211, 164, 83, 0.42);

  font-size: 17px;
}

/* ============================================================
   DARK THEME
============================================================ */

:global(html[data-theme="dark"]) .unlock-character,
:global(.structure-page.dark) .unlock-character {
  --character-background: #303b4c;
  --character-background-light: #27313f;
  --character-color: #88a7ce;
}

:global(html[data-theme="dark"]) .unlock-tone-1,
:global(.structure-page.dark) .unlock-tone-1 {
  --character-background: #39354b;
  --character-background-light: #2e2b3e;
  --character-color: #a69bd3;
}

:global(html[data-theme="dark"]) .unlock-tone-2,
:global(.structure-page.dark) .unlock-tone-2 {
  --character-background: #2d3e4c;
  --character-background-light: #28323d;
  --character-color: #8baec4;
}

:global(html[data-theme="dark"]) .unlock-tone-3,
:global(.structure-page.dark) .unlock-tone-3 {
  --character-background: #304138;
  --character-background-light: #29372f;
  --character-color: #95bfa6;
}

:global(html[data-theme="dark"]) .unlock-level-1,
:global(.structure-page.dark) .unlock-level-1 {
  --character-background: #443b30;
  --character-background-light: #302c29;
  --character-color: #d1af74;
}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {
  .unlock-character,
  .character-silhouette {
    transition: none;
  }
}
</style>
